import SendIcon from '@mui/icons-material/Send';
import { Alert, Box, Stack } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { settingsRoutes } from '../../../../../routes';
import { useCreateScheduleMessage } from '../../../../../sic-lib/api/hooks/useTimeline';
import { ApActionType } from '../../../../../sic-lib/enums/ApActionType';
import { ContactConsent } from '../../../../../sic-lib/types/Contact';
import { sanitizeAllTags } from '../../../../../sic-lib/utils/sanitizeHtml';
import { setContactComposeMessage } from '../../../../../store/conversation';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { useUserHasDialer, useUserHasDialerSetup } from '../../../../../store/users';
import AxiosErrorAlert from '../../../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../../../Shared/ButtonLoading';
import MediaDrawer from '../../../MediaDrawer/MediaDrawer';
import MenuAttachFile from '../../../MenuAttachFiles/MenuAttachFile';
import AiGenerateReply from '../../ConversationAi/AiGenerateReply';
import { MediaItem } from './MediaItem';

interface Props {
    contactId: number;
    clientFieldId: number;
    clientFieldLabel: string;
    clientFieldValue: string;
    userName: string;
    consent: ContactConsent | undefined;
}

export default function ComposeTextMessage({
    contactId,
    clientFieldId,
    clientFieldLabel,
    clientFieldValue,
    userName,
    consent,
}: Props) {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const {
        mutate: scheduleMessage,
        isLoading: isMessageSending,
        isSuccess: isMessageSuccess,
        isError: isMessageError,
        error: messageError,
    } = useCreateScheduleMessage();

    const hasDialer = useUserHasDialer();
    const hasDialerSetup = useUserHasDialerSetup();
    const mediaDrawer = useAppSelector(state => state.media.mediaDrawer);
    const contactComposeMessage = useAppSelector(
        state => state.conversation.contactComposeMessage[contactId],
    );

    const consentStr = useMemo(() => {
        const expiredDate = consent?.impliedConsentExpiry
            ? ` Expired ${new Date(consent?.impliedConsentExpiry).toLocaleDateString()}.`
            : '';
        if (!consent?.expressConsent && !consent?.impliedConsent) {
            return `You do not have telephony consent to message this contact.${expiredDate}`;
        } else if (!consent?.smsConsent) {
            return 'The contact has opted-out of SMS messaging.';
        }
        return undefined;
    }, [consent]);

    const handleSetMessageText = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(
            setContactComposeMessage({
                contactId,
                subject: '',
                body: event.target.value,
                media: contactComposeMessage?.media || [],
            }),
        );
    };

    const handleSend = async () => {
        if (!contactComposeMessage?.body) return;

        scheduleMessage({
            userName: userName,
            label: clientFieldLabel,
            value: clientFieldValue,
            Item: {
                title: 'Direct Plan',
                contactId: contactId,
                actionType: ApActionType.sms,
                clientFieldId: clientFieldId,
                data: {
                    body: sanitizeAllTags(contactComposeMessage.body),
                    subject: '',
                    mediaUrls: contactComposeMessage?.media || [],
                },
            },
        });

        dispatch(setContactComposeMessage({ contactId, subject: '', body: '', media: [] }));
    };

    if (isNaN(clientFieldId))
        return <Alert severity="info">The contact does not have a number to message.</Alert>;

    if (!hasDialer) {
        return (
            <Alert severity="info">
                Texting is not enabled. Please
                <Link
                    target="_blank"
                    rel="noreferrer"
                    href="https://successwebcare.swsecure.com/"
                    underline="hover"
                >
                    {' '}
                    contact support{' '}
                </Link>
                to get started.
            </Alert>
        );
    }

    if (!hasDialerSetup) {
        return (
            <Alert security="info">
                <Link href={settingsRoutes.phoneSettings} underline="hover">
                    Click here to finish setting up your StayInContact phone number.
                </Link>
            </Alert>
        );
    }

    if (!!consentStr) return <Alert severity="warning">{consentStr}</Alert>;

    return (
        <>
            <AxiosErrorAlert error={messageError} />
            <Stack direction="row" spacing={1}>
                <TextField
                    className={classes.txtField}
                    placeholder="Compose a text message..."
                    value={contactComposeMessage?.body || ''}
                    disabled={isMessageSending}
                    onChange={handleSetMessageText}
                    fullWidth
                    multiline
                    maxRows={8}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <AiGenerateReply contactId={contactId} />
                                <MenuAttachFile />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box display="flex" alignItems="center">
                    <ButtonLoading
                        isLoading={isMessageSending}
                        isSuccess={isMessageSuccess}
                        isError={isMessageError}
                        disabled={isMessageSending}
                        onClick={handleSend}
                        successLabel="Sent"
                        className={classes.btnSend}
                        variant="contained"
                        color="secondary"
                    >
                        <SendIcon className={classes.btnSendIcon} />
                    </ButtonLoading>
                </Box>
            </Stack>
            {contactComposeMessage?.media?.length > 0 && (
                <>
                    <Typography variant="body2">Attached files:</Typography>
                    <Stack direction="row" spacing={1}>
                        {contactComposeMessage.media.length > 0 &&
                            contactComposeMessage.media.map((file, index) => (
                                <MediaItem key={index} file={file} contactId={contactId} />
                            ))}
                    </Stack>
                </>
            )}
            {mediaDrawer && <MediaDrawer contactId={contactId} />}
        </>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    txtField: {
        backgroundColor: '#fff',
        border: 'none',
        '& fieldset': {
            border: 'none',
            shadow: 'none',
        },
    },
    btnSend: {
        padding: theme.spacing(1),
        minHeight: '48px',
        minWidth: '48px',
    },
    btnSendIcon: {
        //color: theme.palette.sidebarHeader.main,
    },
}));
